import React, { useState, useEffect } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import CourseYearList from "../courses/CourseList";
import PagesComponent from "../../common/PagesComponent";
import MarkdownViewer from "../../common/MarkdownViewer";
import MCQViewer from "../../common/MCQViewer";
import TrueFalseViewer from "../../common/TrueFalseViewer";
import MatchViewer from "../../common/MatchViewer";
import FillUpsViewer from "../../common/FillUpsViewer";
import AnalysisComponent from "../../common/AnalysisComponent";
import ContentToolbarWrapperComponent from "../../common/ContentToolbarWrapper";
import EvalManualPopup from "../../common/EvalManualPopup";
import EvalScoreDetailsPopup from "../../common/EvalScoreDetailsPopup";
import {
  getPagesForCourse,
  getCourses,
  getPageForCourse,
} from "../../../helper/apicalls/course";
import {
  getCoursePageEvalStatus,
  getCourseEvalPageContent,
  evalWithAiMark,
  createCourseEvalManually,
} from "../../../helper/apicalls/eval";
import "./../../../styles/admin/courses.scss";
import GlobalLoader from "../../common/GlobalLoader";
import EmptyPage from "../../common/emptyPage";
import Cookies from "js-cookie";

const EvalDetails = () => {
  const { key } = useParams();
  const role = Cookies.get("role");
  const { setHeaderName } = useOutletContext();
  const [isTestEnabled, setIsTestEnabled] = useState(false);
  const [isManualEvalOpen, setIsManualEvalOpen] = useState(false);
  const [manualEvalSaving, setManualEvalSaving] = useState(false);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState(null);
  const [cacheSubjectData, setCacheSubjectData] = useState([]);
  const [removeCache, setRemoveCache] = useState(false);
  const [evalStatusByPageId, setEvalStatusByPageId] = useState({});
  const [isEvalScorePopupOpen, setIsEvalScorePopupOpen] = useState(false);

  const getEvalStatusForPage = (page) => {
    if (!page) return null;
    const id = page?.id;
    const key = page?.key;
    return (
      (id && evalStatusByPageId[id]) ||
      (key && evalStatusByPageId[key]) ||
      null
    );
  };

  const isPageEvaluated = (page) => {
    if (!page || !page.id) return false;
    if (page.id === "default-page") return true;
    const status = getEvalStatusForPage(page);
    if (status && typeof status.evaluated !== "undefined") return !!status.evaluated;
    if (page.is_eval_content) return true;
    if (page.evalution_score) return true;
    if (page.overall_score !== null && typeof page.overall_score !== "undefined") return true;
    return false;
  };

  const isContentPage = (page) => {
    if (!page || !page.id) return false;
    if (page.id === "default-page") return false;
    if (page.is_virtual) return false;
    if (Array.isArray(page.child_pages) && page.child_pages.length > 0) return false;
    const type = (page.page_type || "").toLowerCase();
    if (
      type === "quiz" ||
      type === "test" ||
      type === "active_recall" ||
      type === "materials" ||
      type === "material_item"
    )
      return false;
    return true;
  };

  const normalizePageData = (page, payload, extras = {}) => {
    const merged = { ...(page || {}), ...(payload || {}), ...(extras || {}) };
    const normalizedContent =
      typeof merged.content !== "undefined"
        ? merged.content
        : typeof merged.page_content !== "undefined"
          ? merged.page_content
          : typeof merged.pageContent !== "undefined"
            ? merged.pageContent
            : merged.content;

    const normalizedMediumContent =
      typeof merged.medium_content !== "undefined"
        ? merged.medium_content
        : typeof merged.medium_page_content !== "undefined"
          ? merged.medium_page_content
          : typeof merged.mediumContent !== "undefined"
            ? merged.mediumContent
            : merged.medium_content;

    const normalizedHardContent =
      typeof merged.hard_content !== "undefined"
        ? merged.hard_content
        : typeof merged.hard_page_content !== "undefined"
          ? merged.hard_page_content
          : typeof merged.hardContent !== "undefined"
            ? merged.hardContent
            : merged.hard_content;

    return {
      ...merged,
      content: normalizedContent,
      medium_content: normalizedMediumContent,
      hard_content: normalizedHardContent,
    };
  };

  const fetchCourseName = async () => {
    try {
      const courses = await getCourses();

      if (Array.isArray(courses)) {
        const course = courses.find((c) => c.key === key);
        if (course) {
          setHeaderName(`Eval Details - ${course.name}`);
        }
      }
    } catch (err) {
      console.error("Error fetching eval name:", err);
    }
  };

  const handleEvalManuallyOpen = () => {
    if (!selectedPage || !selectedPage.id) return;
    if (selectedPage.id === "default-page") return;
    if (!isContentPage(selectedPage)) return;
    setIsManualEvalOpen(true);
  };

  const handleEvalManuallySave = async (scores) => {
    if (!selectedPage || !selectedPage.id) return;
    if (!isContentPage(selectedPage)) return;
    try {
      setManualEvalSaving(true);
      await createCourseEvalManually({
        key,
        pageId: selectedPage.id,
        evalution_score: scores,
      });
      setIsManualEvalOpen(false);
      await fetchEvalStatus();
      await handlePageSelect(selectedPage);
    } catch (err) {
      console.error("Error saving manual eval:", err);
    } finally {
      setManualEvalSaving(false);
    }
  };

  const fetchPages = async () => {
    try {
      setLoading(true);
      const data = await getPagesForCourse(key);

      if (Array.isArray(data)) {
        setPages(data);
      } else if (
        data &&
        typeof data === "object" &&
        data.hasOwnProperty("id")
      ) {
        setPages([data]);
      } else {
        setPages([]);
      }
    } catch (err) {
      console.error("Error fetching pages:", err);
      setPages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvalStatus = async () => {
    try {
      const status = await getCoursePageEvalStatus(key);
      setEvalStatusByPageId(status?.byPageId || {});
    } catch (err) {
      console.error("Error fetching eval status for course pages:", err);
      setEvalStatusByPageId({});
    }
  };

  const handleToggleTest = () => {
    setIsTestEnabled(!isTestEnabled);
  };

  const handleEvalWithAi = async () => {
    if (!selectedPage || !selectedPage.id) return;
    if (selectedPage.id === "default-page") return;
    if (!isContentPage(selectedPage)) return;
    try {
      setLoading(true);
      const aiResp = await evalWithAiMark({ title: "course", key, pageId: selectedPage.id });
      const aiPayload = aiResp?.data ?? aiResp;

      let evalution_score =
        aiPayload?.structured?.evalution_score ??
        aiPayload?.structured?.evalutionScore ??
        null;

      if (!evalution_score && aiPayload?.content && typeof aiPayload.content === "string") {
        try {
          const parsed = JSON.parse(aiPayload.content);
          evalution_score =
            parsed?.evalution_score ??
            parsed?.evalutionScore ??
            parsed?.evaluation_score ??
            parsed?.evaluationScore ??
            parsed?.scores ??
            null;
        } catch (e) {
          console.log(e);
        }
      }

      if (!evalution_score) {
        evalution_score =
          aiPayload?.evalution_score ??
          aiPayload?.evalutionScore ??
          aiPayload?.evaluation_score ??
          aiPayload?.evaluationScore ??
          aiPayload?.scores ??
          aiPayload?.result?.evalution_score ??
          aiPayload?.result?.evalutionScore ??
          aiPayload?.result?.evaluation_score ??
          aiPayload?.result?.evaluationScore ??
          aiPayload?.result?.scores ??
          null;
      }

      if (evalution_score) {
        await createCourseEvalManually({
          key,
          pageId: selectedPage.id,
          evalution_score,
        });
      }
      await fetchEvalStatus();
      await handlePageSelect(selectedPage);
    } catch (err) {
      console.error("Error running eval with AI:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageSelect = async (page) => {
    if (!page || !page.id) {
      setSelectedPage(page);
      return;
    }

    if (!isContentPage(page)) {
      setSelectedPage(page);
      return;
    }

    const status = evalStatusByPageId[page.id];
    if (status && status.evaluated && page.id !== "default-page") {
      try {
        setLoading(true);
        const evalData = await getCourseEvalPageContent(
          status?.evaluated_id || page.id,
        );
        const payload = evalData?.data ?? evalData;
        if (payload) {
          const overall_score =
            payload.overall_score ??
            payload.overallScore ??
            null;
          const evalution_score =
            payload.evalution_score ??
            payload.evalutionScore ??
            payload.evaluation_score ??
            payload.evaluationScore ??
            payload.scores ??
            null;
          setSelectedPage(
            normalizePageData(page, payload, {
              page_type: payload.page_type || page.page_type,
              is_eval_content: true,
              overall_score,
              evalution_score,
            })
          );
        } else {
          setSelectedPage(page);
        }
      } catch (err) {
        console.error("Error fetching eval content:", err);
        setSelectedPage(page);
      } finally {
        setLoading(false);
      }
    } else {
      if (page.id === "default-page") {
        setSelectedPage(page);
        return;
      }

      try {
        setLoading(true);
        const full = await getPageForCourse(key, page.id);
        const payload = full?.data ?? full;
        if (payload) {
          setSelectedPage(
            normalizePageData(page, payload, {
              is_eval_content: false,
            })
          );
        } else {
          setSelectedPage(page);
        }
      } catch (err) {
        console.error("Error fetching page content:", err);
        setSelectedPage(page);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOpenEvalScoreDetails = async () => {
    if (!selectedPage || !selectedPage.id) return;
    if (selectedPage.id === "default-page") return;
    if (!isContentPage(selectedPage)) return;

    const status = getEvalStatusForPage(selectedPage);
    const evaluated = isPageEvaluated(selectedPage);
    if (!evaluated) return;

    try {
      setLoading(true);
      const evalData = await getCourseEvalPageContent(
        status?.evaluated_id || selectedPage.id,
      );
      const payload = evalData?.data ?? evalData;

      if (payload) {
        const overall_score = payload.overall_score ?? payload.overallScore ?? null;
        const evalution_score =
          payload.evalution_score ??
          payload.evalutionScore ??
          payload.evaluation_score ??
          payload.evaluationScore ??
          payload.scores ??
          null;

        setSelectedPage((prev) =>
          normalizePageData(prev || selectedPage, payload, {
            page_type: payload.page_type || (prev || selectedPage)?.page_type,
            is_eval_content: true,
            overall_score,
            evalution_score,
          })
        );
      }

      setIsEvalScorePopupOpen(true);
    } catch (err) {
      console.error("Error fetching eval content:", err);
      setIsEvalScorePopupOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const renderEvalScoreChips = () => {
    if (!selectedPage || !selectedPage.id) return null;
    if (selectedPage.id === "default-page") return null;
    if (!isContentPage(selectedPage)) return null;

    const status = getEvalStatusForPage(selectedPage);
    const evaluated = isPageEvaluated(selectedPage);
    if (!evaluated) return null;

    const scores = selectedPage.evalution_score || {};
    const overall =
      status && status.overall_score !== null && typeof status.overall_score !== "undefined"
        ? status.overall_score
        : selectedPage.overall_score !== null && typeof selectedPage.overall_score !== "undefined"
          ? selectedPage.overall_score
          : scores.evalution_score;

    const items = [
      { key: "evalution_score", value: overall },
      { key: "quality_score", value: scores.quality_score },
      { key: "relevance_score", value: scores.relevance_score },
      { key: "grounding_score", value: scores.grounding_score },
      { key: "reasoning_score", value: scores.reasoning_score },
      { key: "risk_score", value: scores.risk_score },
    ];

    const formatLabel = (raw) => {
      if (!raw) return "";
      return String(raw)
        .replace(/_/g, " ")
        .split(" ")
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    };

    const toPct = (val) => {
      const num = Number(val);
      if (!Number.isFinite(num)) return 0;
      return Math.max(0, Math.min(100, (num / 10) * 100));
    };

    return (
      <div className="eval-score-actions">
        <button
          type="button"
          className="primary-btn eval-view-details-btn"
          onClick={handleOpenEvalScoreDetails}
        >
          View Evaluate Details
        </button>

        <EvalScoreDetailsPopup
          open={isEvalScorePopupOpen}
          onClose={() => setIsEvalScorePopupOpen(false)}
          overall={overall}
          scores={scores}
        />
      </div>
    );
  };

  const handleUpdatePageContent = (updatedPageData) => {
    // In eval view we do not persist updates, but we keep local state
    setSelectedPage(updatedPageData);
    setRemoveCache(true);
  };

  useEffect(() => {
    fetchCourseName();
    fetchPages();
    fetchEvalStatus();
  }, [key, setHeaderName]);

  return (
    <>
      {loading && <GlobalLoader visible={loading} text="Loading..." />}
      <div className="courses-container">
        <div className="courses-content">
          <div className="course-details-content-right">
            {(() => {
              if (!selectedPage || !selectedPage.id) return null;
              if (selectedPage.id === "default-page") return null;
              if (!isContentPage(selectedPage)) return null;
              const evaluated = isPageEvaluated(selectedPage);
              const shouldShowCreateButton = !evaluated;

              if (!shouldShowCreateButton) return null;

              return (
                <div className="eval-actions-bar">
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={handleEvalWithAi}
                  >
                    Evaluate with AI
                  </button>
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={handleEvalManuallyOpen}
                  >
                    Evaluate Manually
                  </button>
                </div>
              );
            })()}

            {renderEvalScoreChips()}

            <EvalManualPopup
              open={isManualEvalOpen}
              onClose={() => setIsManualEvalOpen(false)}
              onSave={handleEvalManuallySave}
              saving={manualEvalSaving}
            />

            {selectedPage &&
            (selectedPage?.content ||
              selectedPage?.medium_content ||
              selectedPage?.hard_content) ? (
              (() => {
                let contentArray = [];

                try {
                  let contentField = selectedPage?.content;
                  const level = (selectedPage?.level || "").toLowerCase();
                  if (level === "medium" && selectedPage?.medium_content) {
                    contentField = selectedPage?.medium_content;
                  } else if (level === "hard" && selectedPage?.hard_content) {
                    contentField = selectedPage?.hard_content;
                  }

                  if (contentField) {
                    if (typeof contentField === "string") {
                      const trimmed = contentField.trim();
                      if (trimmed) {
                        const parsedContent = JSON.parse(trimmed);
                        if (Array.isArray(parsedContent)) {
                          contentArray = parsedContent;
                        } else if (parsedContent) {
                          contentArray = [parsedContent];
                        }
                      }
                    } else if (Array.isArray(contentField)) {
                      contentArray = contentField;
                    } else if (typeof contentField === "object") {
                      contentArray = [contentField];
                    }
                  }
                } catch (e) {
                  contentArray = [{ content: selectedPage?.content }];
                }

                const hasContent = contentArray && contentArray.length > 0;

                return (
                  <ContentToolbarWrapperComponent
                    onEdit={undefined}
                    onAdd={undefined}
                    pageType={selectedPage?.page_type}
                    permission={false}
                    hasContent={hasContent}
                    disableAdd={selectedPage?.page_type === "quiz"}
                  >
                    <div className="content-wrapper">
                      {contentArray?.length > 0 ? (
                        contentArray.map((item, index) => {
                          let contentType = "content";
                          let contentToDisplay = item;

                          let parsedItem = item;
                          if (typeof item === "string") {
                            try {
                              parsedItem = JSON.parse(item);
                            } catch (e) {
                              parsedItem = item;
                            }
                          }

                          if (
                            parsedItem &&
                            typeof parsedItem === "object" &&
                            parsedItem.type
                          ) {
                            contentType = parsedItem.type;
                            contentToDisplay = parsedItem.content || parsedItem;
                          } else if (
                            item &&
                            typeof item === "object" &&
                            item.type
                          ) {
                            contentType = item.type;
                            contentToDisplay = item.content;
                          } else if (
                            item &&
                            typeof item === "object" &&
                            item.content
                          ) {
                            contentToDisplay = item.content;
                          }
                          switch (contentType) {
                            case "mcq":
                              return (
                                <MCQViewer
                                  key={`mcq-${index}`}
                                  questionData={contentToDisplay}
                                  onUpdate={handleUpdatePageContent}
                                  pageId={selectedPage?.id}
                                  pageData={selectedPage}
                                  context="course"
                                  courseKey={key}
                                  contentIndex={index}
                                  onAddNext={undefined}
                                  permission={false}
                                />
                              );
                            case "trueorfalse":
                              return (
                                <TrueFalseViewer
                                  key={`tf-${index}`}
                                  questionData={contentToDisplay}
                                  onUpdate={handleUpdatePageContent}
                                  pageId={selectedPage?.id}
                                  pageData={selectedPage}
                                  context="course"
                                  courseKey={key}
                                  contentIndex={index}
                                  onAddNext={undefined}
                                  permission={false}
                                />
                              );
                            case "match":
                              return (
                                <MatchViewer
                                  key={`match-${index}`}
                                  questionData={contentToDisplay}
                                  onUpdate={handleUpdatePageContent}
                                  pageId={selectedPage?.id}
                                  pageData={selectedPage}
                                  context="course"
                                  courseKey={key}
                                  contentIndex={index}
                                  onAddNext={undefined}
                                  permission={false}
                                />
                              );
                            case "fillups":
                              return (
                                <FillUpsViewer
                                  key={`fillups-${index}`}
                                  questionData={contentToDisplay}
                                  onUpdate={handleUpdatePageContent}
                                  pageId={selectedPage?.id}
                                  pageData={selectedPage}
                                  context="course"
                                  courseKey={key}
                                  contentIndex={index}
                                  onAddNext={undefined}
                                  permission={false}
                                />
                              );
                            case "analysis":
                              return (
                                <AnalysisComponent
                                  key={`analysis-${index}`}
                                  data={contentToDisplay}
                                  title="Conversational Analysis"
                                  onUpdate={handleUpdatePageContent}
                                  pageId={selectedPage?.id}
                                  pageData={selectedPage}
                                  context="course"
                                  courseKey={key}
                                  contentIndex={index}
                                  onAddNext={undefined}
                                  permission={false}
                                />
                              );
                            default:
                              return (
                                <MarkdownViewer
                                  key={index}
                                  content={
                                    typeof contentToDisplay === "string"
                                      ? contentToDisplay
                                      : typeof contentToDisplay?.markdown === "string"
                                        ? contentToDisplay.markdown
                                        : typeof contentToDisplay?.markdown?.content ===
                                            "string"
                                          ? contentToDisplay.markdown.content
                                          : JSON.stringify(contentToDisplay)
                                  }
                                  pageId={selectedPage?.id}
                                  context="course"
                                  courseKey={key}
                                  onUpdate={handleUpdatePageContent}
                                  permission={false}
                                />
                              );
                          }
                        })
                      ) : (
                        <>
                          <EmptyPage content="No content Available" />
                        </>
                      )}
                    </div>
                  </ContentToolbarWrapperComponent>
                );
              })()
            ) : (
              <CourseYearList
                hideBreadcrumbs={true}
                setCacheSubjectData={setCacheSubjectData}
                cacheSubjectData={cacheSubjectData}
                showActionBtn={false}
                role={role}
              />
            )}
          </div>
          <div className="pages-component-container">
            <PagesComponent
              pages={pages}
              onAddPage={undefined}
              onToggleTest={handleToggleTest}
              isTestEnabled={isTestEnabled}
              addButtonText="Add Page"
              courseKey={key}
              onPageSelect={handlePageSelect}
              selectedPageData={selectedPage}
              removeCache={removeCache}
              setRemoveCache={setRemoveCache}
              readOnly={true}
              evalStatusByPageId={evalStatusByPageId}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default EvalDetails;
